import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Lock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { hasPasswordSpaces } from '../utils/authForms';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token') || '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (password.length < 8) return setError('La contrasena debe tener al menos 8 caracteres.');
    if (hasPasswordSpaces(password) || hasPasswordSpaces(confirmPassword)) return setError('La contrasena no debe contener espacios.');
    if (password !== confirmPassword) return setError('Las contrasenas no coinciden.');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMessage(data.message);
    } catch (err) {
      setError(!err.response ? 'No se pudo conectar con el servidor.' : (err.response?.data?.detail || 'No se pudo restablecer la contrasena.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="mesh-glow-purple" style={{ top: '10%', right: '0%' }} />
      <div className="w-full max-w-md relative z-10">
        <div className="glass-card p-8 rounded-3xl">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Sparkles className="text-[#FF007F] w-8 h-8" />
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>PROVIDER</h1>
          </div>
          <h2 className="text-3xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Outfit' }}>Nueva contrasena</h2>
          <p className="text-sm text-[#9CA3AF] text-center mb-8">Crea una contrasena segura.</p>
          {message && <Notice type="success" text={message} />}
          {error && <Notice type="error" text={error} />}
          <form onSubmit={handleSubmit} className="space-y-5">
            <PasswordField id="password" name="password" label="Nueva contrasena" value={password} onChange={setPassword} visible={showPassword} setVisible={setShowPassword} />
            <PasswordField id="confirmPassword" name="confirmPassword" label="Confirmar contrasena" value={confirmPassword} onChange={setConfirmPassword} visible={showConfirm} setVisible={setShowConfirm} />
            <Button type="submit" disabled={loading || !token} className="w-full gradient-button py-6 rounded-xl">{loading ? 'Guardando...' : 'Cambiar contrasena'}</Button>
          </form>
          <Link to="/login" className="block text-center text-sm text-[#9CA3AF] hover:text-white mt-6">Volver a login</Link>
        </div>
      </div>
    </div>
  );
};

const PasswordField = ({ id, name, label, value, onChange, visible, setVisible }) => (
  <div>
    <Label htmlFor={id} className="text-white mb-2 block">{label}</Label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
      <Input id={id} name={name} type={visible ? 'text' : 'password'} autoComplete="new-password" value={value} onChange={(e) => onChange(e.target.value)} required minLength={8} className="pl-11 pr-11 bg-white/5 border-white/10 rounded-xl text-white" />
      <button type="button" onClick={() => setVisible((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-white" aria-label={visible ? 'Ocultar contrasena' : 'Mostrar contrasena'}>
        {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

const Notice = ({ type, text }) => (
  <div className={`mb-5 p-4 rounded-xl border flex gap-3 ${type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
    {type === 'success' ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
    <p className={`text-sm ${type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{text}</p>
  </div>
);

export default ResetPasswordPage;
